'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { randomId } from '@/lib/random-id';
import type { DocumentKind } from '@/lib/tools-storage';

/** Участник комнаты, как его отдаёт сервер. */
export interface CollabMember {
  id: string;
  name: string;
  isOwner: boolean;
  canEdit: boolean;
}

type ServerEvent =
  | { type: 'state'; content: string; version: number; by: string | null }
  | { type: 'members'; members: CollabMember[] }
  | { type: 'access'; canEdit: boolean; openEditing: boolean }
  | { type: 'closed' };

/** Как часто отправляем правку, пока человек печатает. */
const PUSH_DELAY = 250;

/** Идентификатор участника постоянен в пределах вкладки. */
function participantId(): string {
  if (typeof sessionStorage === 'undefined') return 'anonymous';

  const saved = sessionStorage.getItem('dh-participant');
  if (saved) return saved;

  const id = randomId();
  sessionStorage.setItem('dh-participant', id);
  return id;
}

/**
 * Совместное редактирование документа.
 *
 * Пока комната не создана, хук ничего не делает: одиночная работа в
 * редакторе не должна ни зависеть от сети, ни ждать соединения.
 *
 * Правки уходят пачками раз в четверть секунды, а приходят потоком SSE.
 * Свои же правки сервер обратно не присылает, поэтому курсор при наборе
 * не прыгает.
 */
export function useCollab({
  roomId,
  kind,
  content,
  onRemoteContent,
}: {
  /** Комната, к которой подключены. null — работаем в одиночку. */
  roomId: string | null;
  kind: DocumentKind;
  content: string;
  onRemoteContent: (content: string) => void;
}) {
  const [members, setMembers] = useState<CollabMember[]>([]);
  const [canEdit, setCanEdit] = useState(true);
  const [openEditing, setOpenEditing] = useState(false);
  const [connected, setConnected] = useState(false);
  const [ownerToken, setOwnerToken] = useState<string | null>(null);

  const meRef = useRef<string>('');
  meRef.current ||= participantId();

  // Текст, полученный от других: обратно его отправлять не нужно, иначе
  // два браузера будут бесконечно пересылать друг другу одно и то же.
  const remoteRef = useRef<string | null>(null);
  const onRemoteRef = useRef(onRemoteContent);
  onRemoteRef.current = onRemoteContent;

  // Подписка на события комнаты.
  useEffect(() => {
    if (!roomId) {
      setConnected(false);
      setMembers([]);
      setCanEdit(true);
      return;
    }

    const url = `/api/collab/${roomId}/stream?participant=${encodeURIComponent(meRef.current)}`;
    const source = new EventSource(url);

    source.onopen = () => {
      setConnected(true);
    };

    source.onmessage = (event: MessageEvent<string>) => {
      const data = JSON.parse(event.data) as ServerEvent;

      switch (data.type) {
        case 'state':
          remoteRef.current = data.content;
          onRemoteRef.current(data.content);
          break;
        case 'members':
          setMembers(data.members);
          break;
        case 'access':
          setCanEdit(data.canEdit);
          setOpenEditing(data.openEditing);
          break;
        case 'closed':
          source.close();
          setConnected(false);
          break;
      }
    };

    source.onerror = () => {
      // EventSource переподключается сам, здесь достаточно снять признак:
      // человек должен видеть, что связи сейчас нет.
      setConnected(false);
    };

    return () => {
      source.close();
      setConnected(false);
    };
  }, [roomId]);

  // Отправка своих правок.
  useEffect(() => {
    if (!roomId || !canEdit) return;
    if (content === remoteRef.current) return;

    const timer = setTimeout(() => {
      void fetch(`/api/collab/${roomId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', participant: meRef.current, content }),
      });
    }, PUSH_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [content, roomId, canEdit]);

  /** Открывает комнату для текущего документа и делает нас владельцем. */
  const share = useCallback(
    async (documentId: string, text: string): Promise<string | null> => {
      const response = await fetch('/api/collab', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: documentId, kind, content: text, ownerId: meRef.current }),
      });

      if (!response.ok) return null;

      const data = (await response.json()) as { id: string; ownerToken?: string };
      if (data.ownerToken) setOwnerToken(data.ownerToken);

      return data.id;
    },
    [kind],
  );

  /** Владелец разрешает или запрещает править всем вошедшим. */
  const setOpenEditingRemote = useCallback(
    async (value: boolean) => {
      if (!roomId || !ownerToken) return;

      await fetch(`/api/collab/${roomId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'open-editing', ownerToken, value }),
      });
    },
    [roomId, ownerToken],
  );

  /** Владелец выдаёт или снимает право правки конкретному участнику. */
  const setMemberAccess = useCallback(
    async (member: string, allow: boolean) => {
      if (!roomId || !ownerToken) return;

      await fetch(`/api/collab/${roomId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'member-access', ownerToken, member, allow }),
      });
    },
    [roomId, ownerToken],
  );

  return {
    me: meRef.current,
    members,
    canEdit,
    openEditing,
    connected,
    isOwner: Boolean(ownerToken),
    share,
    setOpenEditing: setOpenEditingRemote,
    setMemberAccess,
  };
}
